import { Field, Form, Formik } from 'formik';
import React, { useCallback } from 'react';
import Pluralize from 'react-pluralize';

import { TextField } from '@sbu/components.textfield';
import { Toggle } from '@sbu/components.toggle';
import { useToasts } from '@sbu/contexts.toasts';
import { DynamicFeatureFlag, useFeatureFlag } from '@sbu/hooks.featureflag';
import { EventPropertyName, TrackEventName } from '@sbu/utils.tracking';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@sumologic/yama-ds-components';

import {
  BulkUpdateEntitySuppressionOperationMutation as BulkUpdateEntitySuppressionOperation,
  BulkUpdateEntitySuppressionOperationMutationVariables as BulkUpdateEntitySuppressionOperationVariables,
} from 'core/graphql';
import { Collapse } from 'shared/components/layout/Collapse';
import { useMutation, useTrackEvent } from 'shared/hooks';

import css from './EntitiesSuppressionEditor.module.scss';
import { BULK_UPDATE_ENTITY_SUPPRESSION_OPERATION } from './mutations';

enum FieldNames {
  COMMENT = 'comment',
  SUPPRESSED = 'suppressed',
}

interface FormValues {
  [FieldNames.COMMENT]: string;
  [FieldNames.SUPPRESSED]: boolean;
}

interface EntitiesSuppressionEditorProps {
  entityIds: string[];
  onClose: () => void;
}

export const EntitiesSuppressionEditor = ({
  entityIds,
  onClose,
}: EntitiesSuppressionEditorProps) => {
  const { length } = entityIds;
  const isEntitiesListTableEnabled = useFeatureFlag(
    DynamicFeatureFlag.EnableEntitiesListTable
  );

  const [updateSuppression, { loading }] = useMutation<
    BulkUpdateEntitySuppressionOperation,
    BulkUpdateEntitySuppressionOperationVariables
  >(BULK_UPDATE_ENTITY_SUPPRESSION_OPERATION);

  const { addSuccess } = useToasts();

  const trackEvent = useTrackEvent();

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      // Only include the comment in the request if `suppressed` is true.
      const { suppressed } = values;
      const comment = suppressed ? values.comment : '';

      // Execute request.
      const result = await updateSuppression({
        variables: { entityIds, comment, whitelistFlag: suppressed },
      });

      if (result && !result.errors) {
        // Notify user.
        addSuccess(
          'The Entity suppression configurations have been updated successfully.'
        );
      }

      if (isEntitiesListTableEnabled) {
        trackEvent(TrackEventName.ENTITIES_BULK_ACTION, {
          [EventPropertyName.ENTITY_COUNT]: entityIds.length,
          [EventPropertyName.ACTION]: 'ChangeSuppression',
        });
      }

      // Close the dialog.
      onClose();
    },
    [
      addSuccess,
      entityIds,
      onClose,
      updateSuppression,
      isEntitiesListTableEnabled,
      trackEvent,
    ]
  );

  return (
    <Dialog
      onClose={onClose}
      open={true}
      maxWidth='sm'
      fullWidth={true}
      aria-labelledby='entities-suppression-editor-dialog-title'
    >
      <DialogTitle id='entities-suppression-editor-dialog-title'>
        Change Suppression for {length.toLocaleString()}{' '}
        <Pluralize
          count={length}
          plural='Entities'
          showCount={false}
          singular='Entity'
        />
      </DialogTitle>
      <Formik<FormValues>
        initialValues={{
          [FieldNames.COMMENT]: '',
          [FieldNames.SUPPRESSED]: false,
        }}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <DialogContent>
              <Field name={FieldNames.SUPPRESSED}>
                {({ field: { value }, form: { handleChange } }) => (
                  <>
                    <div className={css.toggleContainer}>
                      <Toggle
                        label={value ? 'Suppress' : "Don't Suppress"}
                        name={FieldNames.SUPPRESSED}
                        SwitchProps={{
                          checked: value,
                          onChange: handleChange,
                        }}
                      />
                    </div>
                    <Collapse expanded={value}>
                      <div className={css.commentPrompt}>
                        Leave a comment about why you are suppressing{' '}
                        <Pluralize
                          count={length}
                          plural='these Entities'
                          showCount={false}
                          singular='this Entity'
                        />
                        .
                      </div>
                      <TextField
                        disabled={loading}
                        fullWidth
                        multiline
                        name={FieldNames.COMMENT}
                        placeholder='Add an optional comment...'
                      />
                    </Collapse>
                  </>
                )}
              </Field>
            </DialogContent>
            <DialogActions>
              <Button color='secondary' onClick={() => onClose()}>
                Cancel
              </Button>
              <Button disabled={loading} loading={loading} type='submit'>
                Confirm
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
